import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  VStack } from
'@chakra-ui/react';
import {
  Check,
  Search,
  AlertCircle,
  Wrench,
  RefreshCw,
  Send } from
'lucide-react';
const MotionBox = motion(Box);
export function SolutionSection() {
  const steps = [
  {
    icon: Search,
    title: 'Scan your store',
    desc: 'Identify every hidden blocker'
  },
  {
    icon: Wrench,
    title: 'Fix what actually matters',
    desc: 'Not random guesswork'
  },
  {
    icon: RefreshCw,
    title: 'Re-scan to confirm',
    desc: "Make sure you're clean"
  },
  {
    icon: Send,
    title: 'Submit ONE strong appeal',
    desc: 'And get reinstated'
  }];

  return (
    <Box
      as="section"
      py={24}
      bg="brand.card"
      borderY="1px solid"
      borderColor="brand.border">
      
      <Container maxW="container.xl" px={4}>
        {/* How it works */}
        <Box maxW="5xl" mx="auto" mb={32}>
          <Box textAlign="center" mb={16}>
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold"
              mb={4}>
              
              Fix it properly — once.
            </Heading>
            <Text fontSize="xl" color="gray.400">
              The exact workflow to get your account back.
            </Text>
          </Box>

          <Box position="relative">
            <Box
              display={{
                base: 'none',
                md: 'block'
              }}
              position="absolute"
              top={8}
              left={12}
              right={12}
              h="2px"
              bg="brand.border"
              zIndex={0} />
            

            <SimpleGrid
              columns={{
                base: 1,
                md: 4
              }}
              spacing={8}>
              
              {steps.map((step, i) =>
              <MotionBox
                key={i}
                initial={{
                  opacity: 0,
                  y: 20
                }}
                whileInView={{
                  opacity: 1,
                  y: 0
                }}
                viewport={{
                  once: true
                }}
                transition={{
                  delay: i * 0.1
                }}
                position="relative"
                zIndex={10}
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center">
                
                  <Flex
                  w={16}
                  h={16}
                  borderRadius="2xl"
                  bg="brand.dark"
                  border="1px solid"
                  borderColor="rgba(16, 185, 129, 0.3)"
                  align="center"
                  justify="center"
                  mb={6}
                  boxShadow="glow">
                  
                    <step.icon color="#10b981" size={28} />
                  </Flex>
                  <Heading
                  as="h3"
                  fontSize="lg"
                  fontWeight="bold"
                  color="white"
                  mb={2}>
                  
                    {step.title}
                  </Heading>
                  <Text color="gray.400">{step.desc}</Text>
                </MotionBox>
              )}
            </SimpleGrid>
          </Box>
        </Box>

        {/* Pricing / Offer */}
        <Box maxW="3xl" mx="auto">
          <MotionBox
            initial={{
              opacity: 0,
              scale: 0.95
            }}
            whileInView={{
              opacity: 1,
              scale: 1
            }}
            viewport={{
              once: true
            }}
            position="relative"
            borderRadius="3xl"
            border="2px solid"
            borderColor="brand.green"
            bg="brand.dark"
            p={{
              base: 8,
              md: 12
            }}
            textAlign="center"
            boxShadow="glow-strong"
            overflow="hidden">
            
            <Box
              position="absolute"
              top={0}
              left="50%"
              transform="translateX(-50%)"
              w="full"
              h="full"
              bgGradient="linear(to-b, rgba(16, 185, 129, 0.1), transparent)"
              pointerEvents="none" />
            

            <Box position="relative" zIndex={10}>
              <Heading as="h2" fontSize="3xl" fontWeight="bold" mb={2}>
                One-Time Reinstatement Audit
              </Heading>
              <Flex align="baseline" justify="center" gap={2} mb={8}>
                <Text fontSize="5xl" fontWeight="extrabold" color="white">
                  $49
                </Text>
                <Text color="gray.400" fontWeight="medium">
                  one-time
                </Text>
              </Flex>

              <VStack
                spacing={4}
                mb={10}
                align="flex-start"
                maxW="md"
                mx="auto">
                
                {[
                'Full store scan (200+ checks)',
                'Built specifically for suspended accounts',
                'Detects hidden & external issues',
                'Includes 1 free re-scan after fixes',
                'No subscription, ever'].
                map((feature, i) =>
                <Flex key={i} align="flex-start" gap={3}>
                    <Box
                    mt={1}
                    bg="rgba(16, 185, 129, 0.2)"
                    borderRadius="full"
                    p="2px">
                    
                      <Check color="#10b981" size={16} />
                    </Box>
                    <Text color="gray.200" fontWeight="medium">
                      {feature}
                    </Text>
                  </Flex>
                )}
              </VStack>

              <Flex
                mb={8}
                p={4}
                bg="brand.card"
                borderRadius="lg"
                border="1px solid"
                borderColor="brand.border"
                display="inline-flex">
                
                <Text
                  color="brand.green"
                  fontWeight="semibold"
                  display="flex"
                  alignItems="center"
                  gap={2}>
                  
                  <AlertCircle size={18} />
                  Cheaper than another failed appeal.
                </Text>
              </Flex>

              <Button
                display="block"
                w={{
                  base: 'full',
                  sm: 'auto'
                }}
                mx="auto"
                px={10}
                py={5}
                h="auto"
                fontSize="xl"
                fontWeight="bold"
                color="brand.dark"
                bg="brand.green"
                borderRadius="xl"
                _hover={{
                  bg: 'brand.greenHover',
                  boxShadow: 'glow',
                  transform: 'scale(1.05)'
                }}
                transition="all 0.2s">
                
                Check My Store Now
              </Button>
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>);

}